
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle2, 
  Circle, 
  RefreshCw, 
  AlertCircle,
  Database,
  Users,
  Shield
} from "lucide-react";
import { cn } from '@/lib/utils';

interface BackfillStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  icon: React.ComponentType<any>;
  progress?: number;
  error?: string;
}

interface BackfillProgressTrackerProps {
  steps?: BackfillStep[];
  overallProgress?: number;
  isRunning?: boolean;
}

const defaultSteps: BackfillStep[] = [
  {
    id: 'sync',
    title: 'Sync Referred By Affiliate',
    description: 'Update user_profiles.referred_by_affiliate from referrals table',
    status: 'pending',
    icon: RefreshCw
  },
  {
    id: 'backfill',
    title: 'Backfill User Limits',
    description: 'Create missing affiliate_user_limits records',
    status: 'pending',
    icon: Users
  },
  {
    id: 'validate',
    title: 'Validate Consistency',
    description: 'Check data consistency across all tables',
    status: 'pending',
    icon: Shield
  }
];

export const BackfillProgressTracker: React.FC<BackfillProgressTrackerProps> = ({
  steps = defaultSteps,
  overallProgress = 0,
  isRunning = false
}) => {
  const getStepIcon = (step: BackfillStep) => {
    const IconComponent = step.icon;
    
    switch (step.status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStepBadge = (step: BackfillStep) => {
    switch (step.status) {
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'running':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Running</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  const completedSteps = steps.filter(step => step.status === 'completed').length;
  const totalSteps = steps.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <CardTitle>Backfill Progress</CardTitle>
          </div>
          
          {isRunning && (
            <Badge variant="secondary" className="animate-pulse">
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              In Progress
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Overall Progress</span>
            <span className="text-muted-foreground">
              {completedSteps}/{totalSteps} steps completed
            </span>
          </div>
          <Progress value={(completedSteps / totalSteps) * 100} className="h-2" />
        </div>

        {/* Steps List */}
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={cn(
                "flex items-start gap-4 p-4 rounded-lg border transition-all",
                step.status === 'running' && "border-blue-200 bg-blue-50",
                step.status === 'completed' && "border-green-200 bg-green-50",
                step.status === 'error' && "border-red-200 bg-red-50"
              )}
            >
              {/* Step Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {getStepIcon(step)}
              </div>

              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-sm">{step.title}</h4>
                  {getStepBadge(step)}
                </div>
                
                <p className="text-xs text-muted-foreground mb-2">
                  {step.description}
                </p>

                {/* Step Progress */}
                {step.status === 'running' && typeof step.progress === 'number' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Progress</span>
                      <span>{step.progress}%</span>
                    </div>
                    <Progress value={step.progress} className="h-1" />
                  </div>
                )}

                {/* Error Message */}
                {step.status === 'error' && step.error && (
                  <div className="text-xs text-red-600 bg-red-100 p-2 rounded border">
                    {step.error}
                  </div>
                )}
              </div>

              {/* Step Number */}
              <div className="flex-shrink-0">
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                  step.status === 'completed' && "bg-green-500 text-white",
                  step.status === 'running' && "bg-blue-500 text-white",
                  step.status === 'error' && "bg-red-500 text-white",
                  step.status === 'pending' && "bg-muted text-muted-foreground"
                )}>
                  {index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="text-center text-sm text-muted-foreground">
          {isRunning ? (
            <div className="flex items-center justify-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Backfill operation in progress...</span>
            </div>
          ) : completedSteps === totalSteps ? (
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span>All backfill operations completed successfully</span>
            </div>
          ) : (
            <span>Ready to start backfill operations</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
